import styled from "@emotion/styled";
import {D} from "../../Constants";

interface FrameProps {
  alignItems?: 'flex-start' | 'flex-end' | 'center' | 'stretch' | 'baseline';
  justifyContent?: 'flex-start' | 'flex-end' | 'center' | 'space-between' | 'space-around' | 'space-evenly';
  direction?: 'row' | 'column';
  row?: boolean;
  clip?: boolean;
  radius?: number;
  border?: number;
  underline?: number;
  appearance?: 'default' | 'card' | 'error' | 'success' | 'none';
  centerText?: boolean;
  height?: number | string,
  width?: number | string,
  maxWidth?: number | string,
  contentWidth?: boolean,
  padding?: number;
  paddingH?: number;
  paddingV?: number;
  margin?: number | string;
  marginH?: number | string;
  marginV?: number | string;
  marginTop?: number | string;
  marginBottom?: number | string;
  marginLeft?: number | string;
  marginRight?: number | string;
  clickable?: boolean;
  grow?: boolean;
  center?: boolean;
  background?: string;
  noTransition?: boolean;
  left?: boolean;
  wrap?: boolean;
  gap?: number;
  scroll?: boolean;
  hover?: string;
}


const S = function <T extends keyof any>(val: T | undefined, map: Record<T, any>) {
  if (val === undefined) {
    return Object.values(map)[0];
  }

  // @ts-ignore
  return map[val as any];
};

export const Frame = styled.div<FrameProps>`
    display: flex;
    position: relative;
    flex-wrap: ${({wrap}) => wrap ? 'wrap' : 'nowrap'};
    gap: ${({gap}) => gap !== undefined ? D.unit(gap) : undefined};
    flex-grow: ${({grow}) => grow ? 1 : 0};
    flex-basis: ${({grow}) => grow ? 0 : 'auto'};
    height: ${({height}) => typeof height === 'number' ? D.unit(height) : height};
    width: ${({width, contentWidth}) => contentWidth ? `calc(100% - ${D.unit(3)})` : typeof width === 'number' ? D.unit(width) : width};
    max-width: ${({
    maxWidth,
    contentWidth
  }) => contentWidth ? D.maxWidth : typeof maxWidth === 'number' ? D.unit(maxWidth) : maxWidth};
    flex-direction: ${({direction, row}) => row ? 'row' : S(direction, {
    'column': 'column',
    'row': 'row',
  })};
    background: ${({theme, appearance, background}) => background ?? S(appearance, {
    'default': undefined,
    'card': theme.palette.background.paper,
    'error': undefined,
    'success': undefined,
    'none': undefined,
  })};
    border-radius: ${({
    appearance,
    radius
  }) => radius ? D.radius(radius) : appearance === 'none' ? 0 : D.radius(radius)};
    box-shadow: ${({theme, appearance}) => appearance === 'card' ? theme.shadows[4] : undefined};
    border: ${({theme, appearance, border}) => border ? `solid ${border}px ${theme.palette.divider}` : S(appearance, {
    'default': undefined,
    'error': `solid 1px ${theme.palette.error.main}`,
    'success': `solid 1px ${theme.palette.success.main}`,
    'card': undefined,
    'none': undefined,
  })};
    border-bottom: ${({theme, underline}) => underline ? `solid ${underline}px ${theme.palette.divider}` : undefined};
    box-sizing: border-box;
    padding: ${({
    appearance,
    paddingH,
    paddingV,
    padding
  }) => `${D.unit(paddingV ?? padding ?? (appearance === 'card' ? 1 : 0))} ${D.unit(paddingH ?? padding ?? 0)}`};
    margin: ${({
    margin,
    marginTop,
    marginBottom,
    marginLeft,
    marginRight,
    marginH,
    marginV,
    contentWidth
  }) => `${D.unit(marginTop ?? marginV ?? margin ?? 0)} ${D.unit(marginRight ?? marginH ?? margin ?? (contentWidth ? 'auto' : 0))} ${D.unit(marginBottom ?? marginV ?? margin ?? 0)} ${D.unit(marginLeft ?? marginH ?? margin ?? (contentWidth ? 'auto' : 0))}`};


    color: ${({theme, appearance}) => S(appearance, {
    'default': theme.palette.text.primary,
    'error': theme.palette.error.main,
    'success': theme.palette.success.main,
    'card': theme.palette.text.primary,
    'none': theme.palette.text.primary,
  })};
    font-family: "Roboto", "Helvetica", "Arial", sans-serif;
    font-weight: 400;
    font-size: 1rem;
    letter-spacing: 0.00938em;
    text-align: ${({centerText}) => centerText ? 'center' : 'left'};
    transition: ${({noTransition}) => noTransition ? 'none' : 'all 0.3s'};

    overflow: ${({scroll, clip}) => scroll ? 'auto' : clip ? 'hidden' : undefined};

    user-select: ${({clickable}) => clickable ? 'none' : 'auto'};
    cursor: ${({clickable}) => clickable ? 'pointer' : 'auto'};

    align-items: ${({alignItems, center}) => alignItems ?? (center ? 'center' : undefined)};
    justify-content: ${({justifyContent, center}) => justifyContent ?? (center ? 'center' : undefined)};

    :hover {
        background-color: ${({hover}) => hover ? hover : undefined};
    }

    * {
        cursor: ${({clickable}) => clickable ? 'pointer' : undefined};
    }
`;

interface FrameUnitProps extends FrameProps {
  weight?: number;
}

export const FrameUnit = styled(Frame)<FrameUnitProps>`
    flex: ${({weight}) => weight ?? undefined};
`;